import React, { useEffect, useState } from "react";
import { useIngredientApi } from "../../api/IngredientApi";
import type { IngredientInput } from "../../types/Recipe";
import { handleApiError } from "../../utils/handleApiError";
import Select from "react-select";

interface IngredientSelectorProps {
  ingredients: IngredientInput[];
  setIngredients: React.Dispatch<React.SetStateAction<IngredientInput[]>>;
}

const IngredientSelector: React.FC<IngredientSelectorProps> = ({
  ingredients,
  setIngredients,
}) => {
  const [availableIngredients, setAvailableIngredients] = useState<
    { id: number; name: string }[]
  >([]);
  const { getIngredients, createIngredient } = useIngredientApi();

  useEffect(() => {
    (async () => {
      try {
        const response = await getIngredients();
        setAvailableIngredients(response.data.data);
      } catch (err: any) {
        handleApiError(err, "Feil ved lasting av ingredienser.");
      }
    })();
  }, []);

  const addIngredient = () => {
    setIngredients([...ingredients, { id: 0, quantity: 0, unit: "" }]);
  };

  const updateIngredient = (
    index: number,
    field: keyof IngredientInput,
    value: any,
  ) => {
    const updated = [...ingredients];
    (updated[index] as any)[field] = value;
    setIngredients(updated);
  };

  const removeIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const handleNewIngredient = async (name: string, index: number) => {
    if (!name.trim()) return;

    try {
      const response = await createIngredient(name);
      const created = response.data.data;

      // Add new ingredient to list
      setAvailableIngredients((prev) => [...prev, created]);

      // Update row with new ingredient ID
      updateIngredient(index, "id", created.id);

      return created;
    } catch (err: any) {
      // If ingredient already exists, backend returns 409
      if (err.status === 409) {
        const existing = availableIngredients.find(
          (i) => i.name.toLowerCase() === name.toLowerCase(),
        );

        if (existing) {
          updateIngredient(index, "id", existing.id);
          return existing;
        }
      }

      handleApiError(err, "Feil ved oppretting av ny ingrediens.");
    }
  };

  const getOptionsForRow = (currentIndex: number) =>
    availableIngredients.filter(
      (ing) =>
        !ingredients.some((sel, i) => i !== currentIndex && sel.id === ing.id),
    );

  return (
    <div className="border-t pt-3 mt-3">
      <h2 className="font-semibold text-lg text-orange-600 mb-2">
        Ingredienser
      </h2>

      {ingredients.map((ing, index) => (
        <div key={index} className="flex flex-col gap-2 mb-3">
          {/* Ingredient chooser */}
          <Select
            value={
              ing.id && ing.id !== "_new"
                ? {
                    value: ing.id,
                    label:
                      availableIngredients.find((opt) => opt.id === ing.id)
                        ?.name || "",
                  }
                : ing.id === "_new"
                  ? { value: "_new", label: "➕ Lag ny eller søk..." }
                  : null
            }
            onChange={(selected) => {
              if (selected?.value === "_new") {
                updateIngredient(index, "id", "_new");
              } else {
                updateIngredient(index, "id", Number(selected?.value));
              }
            }}
            options={[
              { value: "_new", label: "➕ Lag ny..." },
              ...getOptionsForRow(index).map((opt) => ({
                value: opt.id,
                label: opt.name,
              })),
            ]}
            placeholder="Søk etter ingrediens..."
            isClearable={false}
          />

          {/* New ingredient input */}
          {ing.id === "_new" && (
            <input
              type="text"
              autoFocus
              placeholder="Ny ingrediens"
              onBlur={(e) => handleNewIngredient(e.target.value, index)}
              className="border border-gray-300 rounded-lg px-2 py-1"
            />
          )}

          {/* Quantity and unit */}
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Mengde"
              min={0} // prevents negative numbers in most browsers
              value={ing.quantity ?? ""}
              onChange={(e) => {
                const value = Number(e.target.value);
                updateIngredient(index, "quantity", value < 0 ? 0 : value);
              }}
              className="w-24 border border-gray-300 rounded-lg px-2 py-1"
            />
            <input
              type="text"
              placeholder="Enhet"
              value={ing.unit ?? ""}
              onChange={(e) => updateIngredient(index, "unit", e.target.value)}
              className="w-20 border border-gray-300 rounded-lg px-2 py-1"
            />
            <button
              type="button"
              onClick={() => removeIngredient(index)}
              className="text-red-500 font-bold"
            >
              ✕
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addIngredient}
        className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg hover:bg-orange-200"
      >
        ➕ Legg til ingrediens
      </button>
    </div>
  );
};

export default IngredientSelector;
